import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { Store } from '@ngrx/store';
import { errorNotify, notify } from 'app/store/common-effects/notifier.effects';
import { LoaderService } from 'core/services/loader.service';
import { ownedErrorKey } from 'core/utils/owned-error';
import { of } from 'rxjs';
import { delay, tap } from 'rxjs/operators';

enum HttpCodes {
  BadRequest = 400,
  Unauthorized = 401,
  Forbidden = 403,
  NotFound = 404,
  UnprocessableEntity = 422,
  ServerError = 500,
  GatewayTimeout = 504,
}

@Injectable({ providedIn: 'root' })
export class GlobalErrorHandler implements ErrorHandler {
  constructor(private injector: Injector) {}

  handleError(error: any): void {
    const store$ = this.injector.get(Store);
    const loader = this.injector.get(LoaderService);
    const chunkFailedMessage = /Loading chunk \d+ failed/;

    loader.end();

    if (error instanceof HttpErrorResponse && /(?:^|\/)assets\/translates\/(?:ru|en)\.json(?:[?#]|$)/.test(error.url || '')) {
      console.error('Unable to load application translations:', error);
      return;
    }

    if (chunkFailedMessage.test(error.message)) {
      of(notify({ message: 'NOTIFICATIONS.application_update' }))
        .pipe(
          tap(notification => store$.dispatch(notification)),
          delay(4000),
          tap(() => window.location.reload()),
        )
        .subscribe();
    }
    else if (error instanceof HttpErrorResponse) {
      let message: string | string[];

      switch (error.status) {
        case HttpCodes.BadRequest:
          message = 'ERROR.invalid_request';
          break;

        case HttpCodes.Unauthorized:
          message = 'ERROR.authentication_required';
          break;

        case HttpCodes.Forbidden:
          message = 'ERROR.access_denied';
          break;

        case HttpCodes.NotFound:
          message = 'COMMON.not_found';
          break;

        case HttpCodes.UnprocessableEntity: {
          const keys = Object.keys(error.error);

          message = keys.map((k) => {
            const errors = error.error[k].join(', ');
            return `${k}: ${errors}`;
          });
          break;
        }

        case HttpCodes.ServerError:
          message = 'ERROR.server_error';
          break;

        case HttpCodes.GatewayTimeout:
          message = 'ERROR.timeout';
          break;

        default:
          message = 'ERROR.unknown';
      }

      console.error('Response body:', error);
      store$.dispatch(errorNotify({ message, ...(error.status === HttpCodes.UnprocessableEntity ? { translate: false } : {}) }));
    }
    else {
      console.error('An error occurred:', error);
      const caption = ownedErrorKey(error);
      store$.dispatch(errorNotify({ message: caption || error.message, translate: Boolean(caption) }));
    }
  }
}
